/**
 * Meta Pixel (Facebook Pixel) Tracking Service
 * Full event tracking for RecruitNearMe
 * Pixel ID: 764991906047688
 */

// Check if fbq is available
const isFbqAvailable = () => typeof window !== 'undefined' && typeof window.fbq === 'function';

/**
 * Track a standard Meta Pixel event
 * @param {string} eventName - Standard event name
 * @param {object} params - Event parameters
 */
export const trackPixelEvent = (eventName, params = {}) => {
    if (isFbqAvailable()) {
        window.fbq('track', eventName, params);
        console.log(`[Meta Pixel] ${eventName}`, params);
    }
};

/**
 * Track a custom Meta Pixel event
 * @param {string} eventName - Custom event name
 * @param {object} params - Event parameters
 */
export const trackCustomEvent = (eventName, params = {}) => {
    if (isFbqAvailable()) {
        window.fbq('trackCustom', eventName, params);
        console.log(`[Meta Pixel Custom] ${eventName}`, params);
    }
};

// ============ STANDARD EVENTS ============

/**
 * Track page view (called automatically, but can be called for SPA navigation)
 */
export const trackPageView = () => {
    trackPixelEvent('PageView');
};

/**
 * Track content view (job, agency, blog post)
 * @param {string} contentType - Type of content (job, agency, blog)
 * @param {string} contentId - Unique identifier
 * @param {string} contentName - Name/title of the content
 * @param {number} value - Optional value
 */
export const trackViewContent = (contentType, contentId, contentName, value = 0) => {
    trackPixelEvent('ViewContent', {
        content_type: contentType,
        content_ids: [contentId],
        content_name: contentName,
        value: value,
        currency: 'GBP'
    });
};

/**
 * Track search action
 * @param {string} searchString - The search query
 * @param {string} searchType - Type of search (agencies, jobs)
 * @param {number} resultCount - Number of results
 */
export const trackSearch = (searchString, searchType = 'general', resultCount = 0) => {
    trackPixelEvent('Search', {
        search_string: searchString,
        content_category: searchType,
        num_results: resultCount
    });
};

/**
 * Track user registration
 * @param {string} method - Registration method (email, google)
 * @param {string} status - Registration status
 */
export const trackCompleteRegistration = (method = 'email', status = 'success') => {
    trackPixelEvent('CompleteRegistration', {
        content_name: 'User Registration',
        status: status,
        registration_method: method
    });
};

/**
 * Track lead generation (consultation booking, CV submission)
 * @param {string} leadType - Type of lead (consultation, cv_submission, contact)
 * @param {string} contentName - Description of the lead
 * @param {number} value - Optional value
 */
export const trackLead = (leadType, contentName, value = 0) => {
    trackPixelEvent('Lead', {
        content_category: leadType,
        content_name: contentName,
        value: value,
        currency: 'GBP'
    });
};

/**
 * Track subscription initiation
 * @param {string} tier - Subscription tier (standard, pro)
 * @param {number} value - Price value
 */
export const trackSubscribe = (tier, value) => {
    trackPixelEvent('Subscribe', {
        content_name: `${tier} Subscription`,
        value: value,
        currency: 'GBP',
        predicted_ltv: value * 12 // Annual value
    });
};

/**
 * Track purchase completion
 * @param {string} productName - Name of product/package
 * @param {number} value - Purchase value
 * @param {string} contentType - Type of purchase (subscription, multi_apply)
 */
export const trackPurchase = (productName, value, contentType = 'product') => {
    trackPixelEvent('Purchase', {
        content_name: productName,
        content_type: contentType,
        value: value,
        currency: 'GBP',
        num_items: 1
    });
};

/**
 * Track checkout initiation
 * @param {string} productName - Product being purchased
 * @param {number} value - Cart value
 * @param {string} contentCategory - Category (subscription, multi_apply)
 */
export const trackInitiateCheckout = (productName, value, contentCategory) => {
    trackPixelEvent('InitiateCheckout', {
        content_name: productName,
        content_category: contentCategory,
        value: value,
        currency: 'GBP',
        num_items: 1
    });
};

/**
 * Track add to cart (selecting a plan/package)
 * @param {string} productName - Product name
 * @param {number} value - Product value
 * @param {string} contentType - Type of content
 */
export const trackAddToCart = (productName, value, contentType) => {
    trackPixelEvent('AddToCart', {
        content_name: productName,
        content_type: contentType,
        value: value,
        currency: 'GBP'
    });
};

// ============ CUSTOM EVENTS ============

/**
 * Track agency view
 * @param {string} agencyId - Agency ID
 * @param {string} agencyName - Agency name
 * @param {string} city - Agency city
 */
export const trackAgencyView = (agencyId, agencyName, city = '') => {
    trackViewContent('agency', agencyId, agencyName);
    trackCustomEvent('AgencyView', {
        agency_id: agencyId,
        agency_name: agencyName,
        city: city
    });
};

/**
 * Track job view
 * @param {string} jobId - Job ID
 * @param {string} jobTitle - Job title
 * @param {string} company - Company name
 * @param {string} location - Job location
 */
export const trackJobView = (jobId, jobTitle, company = '', location = '') => {
    trackViewContent('job', jobId, jobTitle);
    trackCustomEvent('JobView', {
        job_id: jobId,
        job_title: jobTitle,
        company: company,
        location: location
    });
};

/**
 * Track blog post view
 * @param {string} postId - Post ID
 * @param {string} title - Post title
 * @param {string} category - Post category
 */
export const trackBlogView = (postId, title, category = '') => {
    trackViewContent('blog', postId, title);
    trackCustomEvent('BlogView', {
        post_id: postId,
        title: title,
        category: category
    });
};

/**
 * Track consultation booking
 * @param {string} consultationType - Type of consultation
 * @param {string} dateTime - Scheduled date/time
 */
export const trackConsultationBooking = (consultationType, dateTime = '') => {
    trackLead('consultation', consultationType, 0);
    trackCustomEvent('ConsultationBooked', {
        consultation_type: consultationType,
        scheduled_datetime: dateTime
    });
};

/**
 * Track CV submission to agency
 * @param {string} agencyId - Agency ID
 * @param {string} agencyName - Agency name
 */
export const trackCVSubmission = (agencyId, agencyName) => {
    trackLead('cv_submission', `CV to ${agencyName}`);
    trackCustomEvent('CVSubmitted', {
        agency_id: agencyId,
        agency_name: agencyName
    });
};

/**
 * Track CV upload
 * @param {string} context - Where CV was uploaded (profile, multi_apply)
 */
export const trackCVUpload = (context = 'profile') => {
    trackCustomEvent('CVUploaded', {
        upload_context: context
    });
};

/**
 * Track multi-apply wizard progress
 * @param {number} step - Current step number
 * @param {string} stepName - Step name
 */
export const trackMultiApplyStep = (step, stepName) => {
    trackCustomEvent('MultiApplyWizardStep', {
        step_number: step,
        step_name: stepName
    });
};

/**
 * Track multi-apply package selection
 * @param {string} packageName - Package name (standard, pro, pro_plus)
 * @param {number} applications - Number of applications
 * @param {number} price - Package price
 */
export const trackMultiApplyPackageSelected = (packageName, applications, price) => {
    trackAddToCart(`Multi-Apply ${packageName}`, price, 'multi_apply');
    trackCustomEvent('MultiApplyPackageSelected', {
        package_name: packageName,
        num_applications: applications,
        value: price
    });
};

/**
 * Track user login
 * @param {string} method - Login method (email, google)
 */
export const trackLogin = (method = 'email') => {
    trackCustomEvent('UserLogin', {
        login_method: method
    });
};

/**
 * Track filter/search usage
 * @param {string} filterType - Type of filter used
 * @param {string} filterValue - Filter value
 */
export const trackFilterUsed = (filterType, filterValue) => {
    trackCustomEvent('FilterUsed', {
        filter_type: filterType,
        filter_value: filterValue
    });
};

/**
 * Track chatbot interaction
 * @param {string} action - Chatbot action (opened, message_sent, whatsapp_handoff)
 */
export const trackChatbotInteraction = (action) => {
    trackCustomEvent('ChatbotInteraction', {
        action: action
    });
};

/**
 * Track pricing page view
 * @param {string} pageType - Type of pricing (subscription, multi_apply)
 */
export const trackPricingView = (pageType = 'subscription') => {
    trackCustomEvent('PricingPageView', {
        pricing_type: pageType
    });
};

// Export all functions
export default {
    trackPageView,
    trackViewContent,
    trackSearch,
    trackCompleteRegistration,
    trackLead,
    trackSubscribe,
    trackPurchase,
    trackInitiateCheckout,
    trackAddToCart,
    trackAgencyView,
    trackJobView,
    trackBlogView,
    trackConsultationBooking,
    trackCVSubmission,
    trackCVUpload,
    trackMultiApplyStep,
    trackMultiApplyPackageSelected,
    trackLogin,
    trackFilterUsed,
    trackChatbotInteraction,
    trackPricingView
};
